import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { formatToken } from '../lib/format';
import type { EngineParameters } from '../lib/lookup-types';
import { atomicToDecimal } from '../state/AppDataContext';

/*
 * Token Liquidity per-token value (owner 2026-09-30: "per token value is not showing" / "what happens to Token
 * Liquidity after tokens burn"). Live value of a token = TOKEN_LIQUIDITY pool x its ratio / weighted active supply
 * (rules 21 s.2 / 23 s.1). Burned and redeemed tokens leave the weighted supply, so the same pool is shared by fewer
 * tokens and every remaining token is worth more; an earnings-tier burn pays nothing out of the pool.
 */
export interface TokenLiquidity { poolAtomic: bigint; weightedBps: bigint; perFullTokenAtomic: bigint | null; valueFor: (ratioBps: number) => bigint | null }

let cache: Promise<EngineParameters> | null = null;
let cachedAt = 0;
function loadParams(): Promise<EngineParameters> {
  if (!cache || Date.now() - cachedAt > 30_000) { cache = api<EngineParameters>('/api/public/onchain/params'); cachedAt = Date.now(); }
  return cache;
}

export function useTokenLiquidity(): TokenLiquidity | null {
  const [state, setState] = useState<TokenLiquidity | null>(null);
  useEffect(() => {
    let cancelled = false;
    loadParams().then((params) => {
      if (cancelled) return;
      const poolAtomic = BigInt(params.vault?.buckets.find((bucket) => bucket.bucket === 'TOKEN_LIQUIDITY')?.balanceAtomic ?? '0');
      const weightedBps = BigInt(params.token?.weightedActiveBps ?? '0');
      const valueFor = (ratioBps: number) => (weightedBps > 0n ? (poolAtomic * BigInt(Math.max(0, Math.round(ratioBps)))) / weightedBps : null);
      setState({ poolAtomic, weightedBps, perFullTokenAtomic: valueFor(10_000), valueFor });
    }).catch(() => { if (!cancelled) setState(null); });
    return () => { cancelled = true; };
  }, []);
  return state;
}

/**
 * The Primary Token's earnings burn tiers as the token contract holds them (reduceThresholdAtomic / burnThresholdAtomic,
 * read live by /api/public/onchain/params); null until read or when the token contract is unavailable.
 */
export type BurnTiers = { reduceAtomic: string; burnAtomic: string; byEra: { era: 'STANDARD' | 'HALF' | 'QUARTER'; reduceAtomic: string; burnAtomic: string }[] };
export function useBurnTiers(): BurnTiers | null {
  const [tiers, setTiers] = useState<BurnTiers | null>(null);
  useEffect(() => {
    let cancelled = false;
    loadParams().then((params) => {
      if (!cancelled) setTiers(params.token ? {
        reduceAtomic: params.token.reduceThresholdAtomic, burnAtomic: params.token.burnThresholdAtomic,
        byEra: (params.token.tiersByEra ?? []).map((t) => ({ era: t.era, reduceAtomic: t.reduceThresholdAtomic, burnAtomic: t.burnThresholdAtomic })),
      } : null);
    }).catch(() => { if (!cancelled) setTiers(null); });
    return () => { cancelled = true; };
  }, []);
  return tiers;
}
/** Rule 40: the thresholds that apply to a token of the given issue era ("STANDARD" / "HALF" / "QUARTER"), as the contract reports them. */
export function tiersForEra(tiers: BurnTiers | null, era: string | null | undefined): { reduceAtomic: string; burnAtomic: string } | null {
  if (!tiers) return null;
  const key = String(era ?? 'STANDARD').toUpperCase();
  return tiers.byEra.find((t) => t.era === key) ?? (key === 'STANDARD' ? { reduceAtomic: tiers.reduceAtomic, burnAtomic: tiers.burnAtomic } : null);
}
/** One line per era: "Standard 50% at 0.00065 · burn at 0.0015". */
export function burnTierLines(tiers: BurnTiers | null, btc: (atomic: string) => string): string[] {
  if (!tiers) return [];
  const rows = tiers.byEra.length ? tiers.byEra : [{ era: 'STANDARD' as const, reduceAtomic: tiers.reduceAtomic, burnAtomic: tiers.burnAtomic }];
  const label = { STANDARD: 'Standard (1X)', HALF: 'Half era (0.5X)', QUARTER: 'Quarter era (0.25X)' };
  return rows.map((t) => `${label[t.era]}: 50% at ${btc(t.reduceAtomic)} · burn at ${btc(t.burnAtomic)}`);
}

export const btcValue = (atomic: bigint | null): string => (atomic === null ? '—' : formatToken(atomicToDecimal(atomic.toString())));
/** "0.5X" / "1X" -> basis points. */
export const ratioToBps = (label: string | null | undefined): number => { const value = parseFloat(String(label ?? '').replace(/x/i, '')); return Number.isFinite(value) ? Math.round(value * 10_000) : 0; };
/** Burn progress of a token: 0% (full), 50% (earnings tier 1), 100% (burned). */
export function burnProgress(originalBps: number, currentBps: number, burned: boolean): string {
  if (burned) return '100%';
  if (originalBps > 0 && currentBps > 0 && currentBps < originalBps) return `${Math.round((1 - currentBps / originalBps) * 100)}%`;
  return '0%';
}

export function TokenLiquiditySummary() {
  const liquidity = useTokenLiquidity();
  if (!liquidity) return null;
  const fullTokens = Number(liquidity.weightedBps) / 10_000;
  return (
    <>
      <dl className="primary-token-grid">
        <div><dt>Token Liquidity fund</dt><dd><strong>{btcValue(liquidity.poolAtomic)}</strong></dd></div>
        <div><dt>Total active tokens (weighted)</dt><dd><strong>{fullTokens.toLocaleString()}</strong> full-token units</dd></div>
        <div><dt>Today's value of one 1X token</dt><dd><strong>{btcValue(liquidity.perFullTokenAtomic)}</strong></dd></div>
        <div><dt>One 0.5X token (50% burned / Half era)</dt><dd>{btcValue(liquidity.valueFor(5_000))}</dd></div>
        <div><dt>One 0.25X token</dt><dd>{btcValue(liquidity.valueFor(2_500))}</dd></div>
      </dl>
      <p className="empty-copy">
        Formula: Token Liquidity fund ÷ total active tokens = today's value of one 1X token; a 50% burned (0.5X) token is worth half of it.
        Fully burned and redeemed tokens are removed from the total, so the same fund is shared by fewer tokens and each remaining token is worth more; a burn by the earnings tier pays nothing out of the fund.
      </p>
    </>
  );
}
